import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, CheckCheck, RefreshCw, SlidersHorizontal } from "lucide-react";
import { errMsg, formatDate as fd, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OpsRulesDialog } from "./OpsRulesDialog";

const SEV = { alta: "bg-red-50 text-red-700 ring-red-200", media: "bg-amber-50 text-amber-800 ring-amber-200" };
const ST = { open: "Da prendere in carico", acked: "Presa in carico", resolved: "Risolto" };

function AlertRow({ a, onAck }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3" data-testid={`ops-alert-${a.id}`}>
      <div className="min-w-0 space-y-0.5">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
          <span className={`rounded px-1.5 py-0.5 text-[11px] ring-1 ${SEV[a.severity] || SEV.media}`} data-testid={`ops-alert-sev-${a.id}`}>{a.severity}</span>{a.title}
        </p>
        <p className="text-xs text-slate-600">{a.tenant_name || a.tenant_id} · valore {a.value} (soglia {a.threshold}) · dal {fd(a.opened_at, true)}{a.reopened ? ` · riaperto ${a.reopened}×` : ""}</p>
        {a.sample_error && <p className="truncate text-xs text-slate-500" data-testid={`ops-alert-err-${a.id}`}>Ultimo errore: {a.sample_error}</p>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500" data-testid={`ops-alert-status-${a.id}`}>{ST[a.status]}</span>
        {a.status === "open" && <Button size="sm" variant="outline" onClick={() => onAck(a.id)} data-testid={`ops-alert-ack-${a.id}`}><CheckCheck size={14} className="mr-1" />Prendi in carico</Button>}
      </div>
    </li>
  );
}

// MUSORA internal admins only: operational alerts shown in-app, never sent by email/WhatsApp.
export function OpsAlertsPanel() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [state, setState] = useState("active");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/ops-alerts", { params: { state } }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [api, state]);
  useEffect(() => { load(); }, [load]);
  const check = async () => { setBusy(true); try { const { data } = await api.post("/admin/ops-alerts/evaluate"); toast.success(`Controllo eseguito: ${data.opened} nuovi, ${data.resolved} risolti`); await load(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const ack = async (id) => { try { await api.post(`/admin/ops-alerts/${id}/ack`); await load(); } catch (e) { toast.error(errMsg(e)); } };
  const s = d?.summary || {};
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="ops-alerts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><BellRing size={16} />Avvisi operativi</h2>
          <p className="text-xs text-slate-500" data-testid="ops-alerts-summary">{s.active ?? "…"} attivi · {s.unacked ?? "…"} da prendere in carico · {s.high ?? "…"} gravi · ultimo controllo {s.last_eval_at ? fd(s.last_eval_at, true) : "mai"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={state} onChange={(e) => setState(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="ops-alerts-filter">
            <option value="active">Attivi</option><option value="resolved">Risolti</option><option value="all">Tutti</option>
          </select>
          <Button size="sm" variant="outline" onClick={check} disabled={busy} data-testid="ops-alerts-check"><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Controlla ora</Button>
          <Button size="sm" variant="outline" onClick={() => setRulesOpen(true)} data-testid="ops-alerts-rules-btn"><SlidersHorizontal size={14} className="mr-1" />Soglie</Button>
        </div>
      </div>
      {!d ? <p className="px-4 py-3 text-sm text-slate-500">Caricamento…</p> : !d.items.length
        ? <p className="px-4 py-3 text-sm text-slate-500" data-testid="ops-alerts-empty">Nessun avviso {state === "active" ? "attivo" : ""}.</p>
        : <ul className="divide-y divide-slate-100" data-testid="ops-alerts-list">{d.items.map((a) => <AlertRow key={a.id} a={a} onAck={ack} />)}</ul>}
      <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500" data-testid="ops-alerts-channels">Solo in questo pannello interno: nessuna email o WhatsApp viene inviata. {s.whatsapp_note}</p>
      {rulesOpen && d && <OpsRulesDialog rules={d.rules} onClose={() => setRulesOpen(false)} onSaved={load} />}
    </section>
  );
}
